import React from 'react';
import { Box, Typography, Container, Chip, Button, Grid, CircularProgress } from '@mui/joy';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { usePublishedBlogs } from './useBlogs';

const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const BlogPage = () => {
  const posts = usePublishedBlogs();
  const featured = posts ? posts[0] : null;
  const recent = posts ? posts.slice(1, 4) : [];

  if (posts === null) {
    return (
      <Box sx={{ py: 12, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 12, minHeight: '100vh' }}>
      <Helmet>
        <title>Blog | Calvet Technology Solutions</title>
        <meta
          name="description"
          content="Insights on AI, software development, cloud infrastructure, and digital transformation from Calvet Technology Solutions."
        />
        <meta property="og:title" content="Blog | Calvet Technology Solutions" />
        <meta
          property="og:description"
          content="Insights on AI, software development, cloud infrastructure, and digital transformation from Calvet Technology Solutions."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <Container maxWidth="lg">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              level="h1"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Insights & Articles
            </Typography>
            <Typography
              level="h4"
              sx={{
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Practical perspectives on AI, software engineering, cloud infrastructure,
              and building technology that drives real business results.
            </Typography>
          </Box>
        </motion.div>

        {/* Featured Article */}
        {featured && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <Link to={`/blog/${featured.slug}`} style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  p: { xs: 4, md: 6 },
                  mb: 8,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: '1px solid rgba(46, 91, 186, 0.4)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                {/* Featured badge */}
                <Chip
                  size="sm"
                  variant="solid"
                  sx={{
                    mb: 3,
                    background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  LATEST ARTICLE
                </Chip>

                <Typography
                  level="body2"
                  sx={{
                    color: '#2E5BBA',
                    fontWeight: 600,
                    mb: 2,
                    fontSize: '0.9rem',
                  }}
                >
                  {formatDate(featured.date)}
                </Typography>

                <Typography
                  level="h2"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                    color: '#ffffff',
                    lineHeight: 1.3,
                  }}
                >
                  {featured.title}
                </Typography>

                <Typography
                  level="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    mb: 4,
                    maxWidth: 800,
                    fontSize: { xs: '1rem', md: '1.125rem' },
                  }}
                >
                  {featured.excerpt}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {featured.tags && featured.tags.map((tag) => (
                      <Chip
                        key={tag}
                        size="sm"
                        variant="soft"
                        sx={{
                          background: 'rgba(46, 91, 186, 0.1)',
                          color: '#2E5BBA',
                          border: '1px solid rgba(46, 91, 186, 0.2)',
                          fontSize: '0.75rem',
                        }}
                      >
                        {tag}
                      </Chip>
                    ))}
                  </Box>

                  <Typography
                    level="body2"
                    sx={{
                      color: '#2E5BBA',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      ml: 'auto',
                    }}
                  >
                    Read Article <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
                  </Typography>
                </Box>

                {/* Decorative gradient accent */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: 'linear-gradient(180deg, #2E5BBA 0%, #00A86B 100%)',
                  }}
                />
              </Box>
            </Link>
          </motion.div>
        )}

        {/* Recent Articles Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography
              level="h3"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Recent Articles
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {recent.map((post, index) => (
            <Grid key={post.slug} xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                whileHover={{ y: -5 }}
                style={{ height: '100%' }}
              >
                <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                  <Box
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                      },
                    }}
                  >
                    <Typography
                      level="body2"
                      sx={{
                        color: '#2E5BBA',
                        fontWeight: 600,
                        mb: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      {formatDate(post.date)}
                    </Typography>

                    <Typography
                      level="h4"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: '#ffffff',
                        lineHeight: 1.4,
                      }}
                    >
                      {post.title}
                    </Typography>

                    <Typography
                      level="body2"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        mb: 3,
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {post.excerpt}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {post.tags && post.tags.map((tag) => (
                        <Chip
                          key={tag}
                          size="sm"
                          variant="soft"
                          sx={{
                            background: 'rgba(46, 91, 186, 0.1)',
                            color: '#2E5BBA',
                            border: '1px solid rgba(46, 91, 186, 0.2)',
                            fontSize: '0.75rem',
                          }}
                        >
                          {tag}
                        </Chip>
                      ))}
                    </Box>
                  </Box>
                </Link>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* View All Articles CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={Link}
              to="/blog/all"
              variant="outlined"
              size="lg"
              endDecorator={<ArrowForwardIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'text.primary',
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  borderColor: '#2E5BBA',
                  color: '#2E5BBA',
                  background: 'rgba(46, 91, 186, 0.05)',
                },
              }}
            >
              View All Articles
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default BlogPage;
